
// This overrides the PIDFields component because the default one is
// confusing.
//
// Copyright (C) 2025 Northwestern University
//
// Prism is all rights reserved.

import React, { useEffect, useState, Fragment } from "react";
import { FastField, getIn } from "formik";
import _get from "lodash/get";
import { Form, Radio } from "semantic-ui-react";
import { FieldLabel } from "react-invenio-forms";
import { PIDField } from "@js/invenio_rdm_records";


// Constants
const RADIO_DOI_NO = "radio_doi_no";
const RADIO_DOI_YES = "radio_doi_yes";
const PROVIDER_EXTERNAL = "external";


function DOIInputField(props) {
  // Props
  const { error, fieldPath, form, visible, value } = props;

  // Specific to this component
  const placeholder = "Copy/paste your existing DOI here...";

  // derived
  // Can't change an internal (managed) DOI
  const provider = value.provider || PROVIDER_EXTERNAL;  // to be safe
  const isEnabled = visible && provider === PROVIDER_EXTERNAL;

  // Updates
  function handleChange(e, { value }) {
    const newDOI = {
      identifier: value,
      provider: PROVIDER_EXTERNAL, // in keeping with InvenioRDM decision
    };

    form.setFieldValue(fieldPath, newDOI);
  }

  return (
    <Form.Field width={8} error={error}>
      <Form.Input
        aria-label="External DOI"
        className={!visible ? "hide" : undefined}
        disabled={!isEnabled}
        onChange={handleChange}
        value={value.identifier || ""}
        placeholder={placeholder}
        width={16}
        error={error}
      />
    </Form.Field>
  );
}


function getFieldErrors(form, fieldPath) {
  return (
    getIn(form.errors, fieldPath, null) || getIn(form.initialErrors, fieldPath, null)
  );
};


/**
 * Crux of the component that overrides the default DOI component.
 * Our component was created to have less confusing UI.
 *

 * @param {object} props
 * @return {React.Component}
*/
function DOIField(props) {
  const fieldPath = props.field.name;

  // State
  //
  // We can't let Formik hold the state completely since clicking one of
  // the radio button option is a purely UI state
  // Set the initial doiOptionClicked value according to initial form values.
  const [doiOptionClicked, setDoiOptionClicked] = useState(
    () => {
      const idOfInitialDOI = _get(props.form.initialValues, fieldPath, {}).identifier;
      return idOfInitialDOI ? RADIO_DOI_YES : RADIO_DOI_NO;
    }
  );

  const [savedDOI, setSavedDOI] = useState(
    // Making sure that savedDOI has correct values for when they are set
    () => {
      const doiInitial = _get(props.form.initialValues, fieldPath, {});

      return {
        // Being very safe with whatever is given to us
        // On the right are the InvenioRDM default values
        ...doiInitial,
        identifier: doiInitial?.identifier || "",
        provider: doiInitial?.provider || PROVIDER_EXTERNAL
      }
    }
  );

  // Effect
  //
  // Need to reset form values to remove pids.doi completely if no identifier
  // The default initial pids for a new draft is:
  // "pids": {
  //   "doi": {
  //     "identifier": "",
  //     "provider": "external"
  //    }
  //  },
  // which actually should not exist at all.
  // Changing the form.values.pids.doi cannot be done in use State initializing
  // function because it's a side-effect.
  useEffect(
    () => {
      const idOfInitialDOI = _get(props.form.initialValues, fieldPath, {}).identifier;
      if (!idOfInitialDOI) {
        const { doi, ...otherPids } = props.form.initialValues.pids;
        props.form.setFieldValue("pids", otherPids);
      }
    },
    []  // makes sure it's only run once
  );

  // Props
  const {
    // Injected by Formik
    form,
    // Passed by parent component
    record,
  } = props;

  // Specific to this component
  const pidIcon = "barcode";
  const required = true;
  const helpText = "A DOI allows your upload to be easily and unambiguously cited. Example: '10.1234/foo.bar'. Upon publication, a DOI will be minted for you if you don't already have one.";

  // Derived
  const fieldError = getFieldErrors(form, fieldPath);
  const radioDisabled = record.is_published === true;

  // Handlers
  function handleRadioChange(e, { value }) {
    // Split out doi section from pids
    const { doi, ...otherPids } = form.values.pids || {};
    // value is new value
    switch (value) {
      case RADIO_DOI_NO:
        setDoiOptionClicked(RADIO_DOI_NO);
        setSavedDOI(doi);
        form.setFieldValue("pids", otherPids);
        break;

      case RADIO_DOI_YES:
        setDoiOptionClicked(RADIO_DOI_YES);
        form.setFieldValue(fieldPath, savedDOI);
        break;

      default:
        console.error("Invalid value", value);
        break;
    }
  }

  // Render
  return (
    <>
      <Form.Field required={required} error={fieldError}>
        <FieldLabel htmlFor={fieldPath} icon={pidIcon} label={"Digital Object Identifier (DOI)"} />
      </Form.Field>

      <Form.Field>
        Do you already have a DOI for this upload?
      </Form.Field>

      <Form.Field width={2}>
        <Radio
          id="doi-radio-no"
          label="No"
          name="radioGroup"
          value={RADIO_DOI_NO}
          disabled={radioDisabled}
          checked={RADIO_DOI_NO === doiOptionClicked}
          onChange={handleRadioChange}
        />
      </Form.Field>

      <Form.Group inline className="mb-0">
        <Form.Field>
          <Radio
            id="doi-radio-yes"
            label="Yes"
            name="radioGroup"
            value={RADIO_DOI_YES}
            disabled={radioDisabled}
            checked={RADIO_DOI_YES === doiOptionClicked}
            onChange={handleRadioChange}
            width={4}
          />
        </Form.Field>
        <DOIInputField
          fieldPath={fieldPath}
          visible={RADIO_DOI_YES === doiOptionClicked}
          value={form.values.pids.doi || {}}
          form={form}
          error={fieldError}
        />
      </Form.Group>

      {helpText && <label className="helptext">{helpText}</label>}
    </>
  );
}

/**
 * Dynamically return a generic PIDField or our custom DOIField.
 *
 * @param {object} configOfPID :
 *    {string} scheme
 * @param {object} record
 * @return {React.Component}
 */
function selectPIDField(configOfPID, record) {
  // Render
  if (configOfPID.scheme === "doi") {
    const fieldPath = `pids.${configOfPID.scheme}`;
    const componentProps = { configOfPID, record };
    return (
      <Fragment key={configOfPID.scheme}>
        <FastField name={fieldPath} component={DOIField} {...componentProps} />
      </Fragment>
    );
  } else {
    // We return the original PIDField component
    // Based on PIDField definition and usage:
    // (links indexed on InvenioRDMv13 compatible modules)
    // definition: https://github.com/inveniosoftware/invenio-rdm-records/blob/maint-19.x/invenio_rdm_records/assets/semantic-ui/js/invenio_rdm_records/src/deposit/fields/Identifiers/PIDField/PIDFieldCmp.js
    // usage: https://github.com/inveniosoftware/invenio-app-rdm/blob/maint-v13.x/invenio_app_rdm/theme/assets/semantic-ui/js/invenio_app_rdm/deposit/RDMDepositForm.js#L234
    // WARNING: Original usage assumes that PIDField is for DOI only in v13.
    //          It introduces doi-specific props which we omit:
    //          - optionalDOItransitions={configOfPID.optional_doi_transitions}
    //          - doiDefaultSelection={pid.default_selected}
    //          - required={this.config.is_doi_required}
    return (
      <Fragment key={configOfPID.scheme}>
        <PIDField
          btnLabelDiscardPID={configOfPID.btn_label_discard_pid}
          btnLabelGetPID={configOfPID.btn_label_get_pid}
          canBeManaged={configOfPID.can_be_managed}
          canBeUnmanaged={configOfPID.can_be_unmanaged}
          fieldPath={`pids.${configOfPID.scheme}`}
          fieldLabel={configOfPID.field_label}
          isEditingPublishedRecord={
            record.is_published === true // is_published is `null` on first upload
          }
          managedHelpText={configOfPID.managed_help_text}
          pidLabel={configOfPID.pid_label}
          pidPlaceholder={configOfPID.pid_placeholder}
          pidType={configOfPID.scheme}
          unmanagedHelpText={configOfPID.unmanaged_help_text}
          required // usage has config.is_doi_required which we don't reproduce
          record={record}
        />
      </Fragment>
    );
  }
}


/**
 * The top-level component to render all PID related form fields.
 *
 * @export
 * @param {object} props : {
 *   {object} config: {
 *     {Array} pids: array of configs (1 for each PID)
 *   },
 *   {object} record: JS object representation of record
 * @return {Array} of JSX elements
 */
export function PIDFields(props) {
  // Props
  const { config, record } = props;

  return config.pids.map((configOfPID) => selectPIDField(configOfPID, record));
}
